import React, { useRef } from 'react';
import t from 'react-translate';
import { QuizQuestionType } from 'redux/schemas/models/quiz';
import { css } from '@emotion/react';
import { halfSpacing } from 'styles/global_defaults/scaffolding';
import { ComponentType } from 'redux/schemas/models/lecture-component';
import { QuizRichTextElement, QuizSimpleTextComponent, getTablePosition } from './helper';

type EditableProps = {
  componentType: ComponentType,
  question,
  questionIndex: number,
  option,
  optionIndex: number,
  containerClass: string,
  setOptionText: (questionIndex: number, optionIndex: number, newDescription: string) => void,
  setFeedBackText?: (questionIndex: number, optionIndex: number, newDescription: string, isSecondary?: boolean) => void,
};

const EditableOption = ({
  componentType,
  question,
  questionIndex,
  option,
  optionIndex,
  containerClass,
  setOptionText,
  setFeedBackText,
}: EditableProps) => {
  const optionStyle = css`
    .option-component {
      margin-top: ${halfSpacing}px;
    }
  `;

  const optionRef = useRef(option);
  const originalOption = optionRef.current;
  const originalOptionDisabled = !originalOption.optionContent?.trim().length;

  const getComponent = () => {
    let component = null;
    switch (question.type) {
      case QuizQuestionType.TABLE_NUMBER_QUESTION:
      case QuizQuestionType.TABLE_SHORT_ANSWER_QUESTION:
        if (option.readOnly) {
          component = (
            <React.Fragment>
              <div className='tag bg-gray-6 text-small font-weight-bolder'>
                { t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.TABLE_LABEL(`${getTablePosition(question.numColumns, optionIndex)}`) }
              </div>
              <QuizSimpleTextComponent
                value={option.optionContent}
                onChange={(newDescription) => { setOptionText(questionIndex, optionIndex, newDescription); }}
                disabled={originalOptionDisabled}
              />
            </React.Fragment>
          );
        } else {
          component = option.explanation || option.secondaryExplanation
            ? (
              <React.Fragment>
                {
                  originalOption.explanation && (
                    <React.Fragment>
                      <div className='tag bg-gray-6 text-small font-weight-bolder'>
                        { t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.TABLE_CELL(`${getTablePosition(question.numColumns, optionIndex)}`, 'true') }
                      </div>
                      <QuizRichTextElement
                        onChange={(newDescription) => { setFeedBackText(questionIndex, optionIndex, newDescription); }}
                        value={option.explanation}
                        containerClass={containerClass}
                      />
                    </React.Fragment>
                  )
                }
                {
                  originalOption.secondaryExplanation && (
                    <React.Fragment>
                      <div className='tag bg-gray-6 text-small font-weight-bolder'>
                        { t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.TABLE_CELL(`${getTablePosition(question.numColumns, optionIndex)}`, 'false') }
                      </div>
                      <QuizRichTextElement
                        onChange={(newDescription) => { setFeedBackText(questionIndex, optionIndex, newDescription, true); }}
                        value={option.secondaryExplanation}
                        containerClass={containerClass}
                      />
                    </React.Fragment>
                  )
                }
              </React.Fragment>
            )
            : null;
        }
        break;
      case QuizQuestionType.MULTIPLE_CHOICE:
        component = (
          <React.Fragment>
            <div className='tag bg-gray-6 text-small font-weight-bolder'>
              { t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.OPTION(`${optionIndex + 1}`)}
            </div>
            {
              componentType === ComponentType.SURVEY
                ? (
                  <QuizSimpleTextComponent
                    onChange={(newDescription) => { setOptionText(questionIndex, optionIndex, newDescription); }}
                    value={option.optionContent}
                    disabled={originalOptionDisabled}
                  />
                )
                : (
                  <QuizRichTextElement
                    onChange={(newDescription) => { setOptionText(questionIndex, optionIndex, newDescription); }}
                    value={option.optionContent}
                    containerClass={containerClass}
                    disabled={originalOptionDisabled}
                  />
                )
            }
            {
              originalOption.explanation && (
                <React.Fragment>
                  <div className='tag bg-gray-6 text-small font-weight-bolder'>
                    {t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.FEEDBACK(`${optionIndex + 1}`)}
                  </div>
                  <QuizRichTextElement
                    onChange={(newDescription) => { setFeedBackText(questionIndex, optionIndex, newDescription); }}
                    value={option.explanation}
                    containerClass={containerClass}
                  />
                </React.Fragment>
              )
            }
          </React.Fragment>
        );
        break;
      case QuizQuestionType.MULTIPLE_CHOICE_MULTIPLE_ANSWER:
        component = (
          <React.Fragment>
            <div className='tag bg-gray-6 text-small font-weight-bolder'>
              { t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.OPTION(`${optionIndex + 1}`)}
            </div>
            {
              componentType === ComponentType.SURVEY
                ? (
                  <QuizSimpleTextComponent
                    onChange={(newDescription) => { setOptionText(questionIndex, optionIndex, newDescription); }}
                    value={option.optionContent}
                    disabled={originalOptionDisabled}
                  />
                )
                : (
                  <QuizRichTextElement
                    onChange={(newDescription) => { setOptionText(questionIndex, optionIndex, newDescription); }}
                    value={option.optionContent}
                    containerClass={containerClass}
                    disabled={originalOptionDisabled}
                  />
                )
            }
            {
              originalOption.explanation && (
                <React.Fragment>
                  <div className='tag bg-gray-6 text-small font-weight-bolder'>
                    {t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.FEEDBACK(`${optionIndex + 1}`)}
                  </div>
                  <QuizRichTextElement
                    onChange={(newDescription) => { setFeedBackText(questionIndex, optionIndex, newDescription); }}
                    value={option.explanation}
                    containerClass={containerClass}
                  />
                </React.Fragment>
              )
            }
          </React.Fragment>
        );
        break;
      case QuizQuestionType.ALL_QUESTION_VIEW_SHORT_TEXT_QUESTION:
      case QuizQuestionType.LONG_ANSWER_QUESTION:
      case QuizQuestionType.LIKERT_SCALE_QUESTION:
      case QuizQuestionType.NUMBER_QUESTION:
        component = option.explanation || option.secondaryExplanation
          ? (
            <div className='feedback'>
              {
                originalOption.explanation && (
                  <React.Fragment>
                    <div className='tag bg-gray-6 text-small font-weight-bolder'>
                      { `${
                        question.type === QuizQuestionType.LONG_ANSWER_QUESTION
                          ? t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.FEEDBACK_FOR_ANY_ANSWER()
                          : t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.FEEDBACK_CORRECT()
                      }`}
                    </div>
                    <QuizRichTextElement
                      value={option.explanation}
                      onChange={(newDescription) => { setFeedBackText(questionIndex, optionIndex, newDescription); }}
                      containerClass={containerClass}
                    />
                  </React.Fragment>
                )
              }
              {
                originalOption.secondaryExplanation && (
                  <React.Fragment>
                    <div className='tag bg-gray-6 text-small font-weight-bolder'>
                      { `${t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.FEEDBACK_INCORRECT()}`}
                    </div>
                    <QuizRichTextElement
                      value={option.secondaryExplanation}
                      onChange={(newDescription) => { setFeedBackText(questionIndex, optionIndex, newDescription, true); }}
                      containerClass={containerClass}
                    />
                  </React.Fragment>
                )
              }
            </div>
          )
          : null;
        break;
      case QuizQuestionType.MATCHING_QUESTION:
        component = (
          <React.Fragment>
            <div className='tag bg-gray-6 text-small font-weight-bolder'>
              { t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.PAIR(((optionIndex + 1) % 2 === 1).toString(), `${Math.floor(1 + ((optionIndex) / 2))}`)}
            </div>
            <QuizRichTextElement
              onChange={(newDescription) => { setOptionText(questionIndex, optionIndex, newDescription); }}
              value={option.optionContent}
              containerClass={containerClass}
              disabled={originalOptionDisabled}
            />
          </React.Fragment>
        );
        break;
      default:
        break;
    }
    return (component);
  };

  return (
    <div className='option' css={optionStyle}>
      {
        !!getComponent() && (
          <div className='option-component'>
            {getComponent()}
          </div>
        )
      }
    </div>
  );
};

export default EditableOption;
